import { AdobeIMS } from '@identity/imslib';
// React Imports
import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Connected Redux Component
import { connect } from 'react-redux';

// i18n
import { Translate } from 'react-redux-i18n';

// React Spectrum Components
import Button from '@react/react-spectrum/Button';
import Wait from '@react/react-spectrum/Wait';
import Dropdown from '@react/react-spectrum/Dropdown';
import { Menu, MenuItem } from '@react/react-spectrum/Menu';
import AdobeLogo from '@react/react-spectrum/Icon/AdobeLogo';

// Redux actions
import { signIn, signOut, addProfile } from './actions/imsActions';

import {fetchProfile} from './userProfile'

import { getPpsProfileConfig } from '../../config/ppsProfileUrls'

// Styles
import './css/AdobeIMS.scss';

/**
 * Component for handling the IMS login.
 *
 * This component is meant to work with a Redux store, and requires a .env file with the
 * following variables: REACT_APP_IMS_CLIENT and REACT_APP_IMS_ENV for the client ID and
 * IMS environment, respectively. It expects a signIn and signOut Redux action, and
 * makes use of React Spectrum components.
 *
 * @component
 * @example
 * const mandatorySignIn = true;
 * const susiLocale = 'en_US';
 * const afterSignInCallback = (profile) => console.log(profile);
 * return (
 *   <AdobeIMS
 *    mandatorySignIn={mandatorySignIn}
 *    susiLocale={susiLocale}
 *    afterSignInCallback={afterSignInCallback} />
 * )
 */
class AdobeIMSV2 extends Component {

  constructor (props){
    super();
    const {
      mandatorySignIn, susiLocale, afterSignInCallback, signInAction, signOutAction,addProfileAction
    } = props;
      // Define our IMS configuration
      this.imsData = {
          client_id: window.apcConfig.ims.client,
          scope: 'AdobeID,openid,pps.read',
          locale: susiLocale,
          imsReady: false,
          environment: window.apcConfig.ims.environment,
          redirect_uri : window.location.href,
          onReady: async () => {
            // Dispatch a window event
            window.dispatchEvent(new Event('adobeIMS:ready'));
            window.adobeid.imsReady = true
          },
          onAccessTokenHasExpired: () => {
            // Clear the sign in state in our Redux store.
            signOutAction();
          },
          onProfile: async function(profile) { 
            addProfileAction(profile);
              // We are signed in.
              // Try to fetch the user's avatar
              try {
                const ppsConfig = getPpsProfileConfig();
                const fetchOptions = {
                  headers: {
                    Authorization: `Bearer ${window.adobeIMS.getAccessToken().token}`,
                    'x-api-key': ppsConfig.apiKey,
                  },
                };
                const response = await fetch(ppsConfig.endpoint, fetchOptions);
                const data = await response.json();
                // Map PPS response (images keyed by ratio) to the shape the app expects
                // (user.avatar); pick the 138 ratio for the avatar.
                signInAction({ user: { avatar: data?.images?.['138'] } });
              } catch (e) {
                // If there was a problem fetching the avatar, default to basic profile icon
                signInAction();
                window.console.error(e);
                if (window.newrelic) {
                  let profile;
                  adobeIMS.getProfile().then(response => {
                    profile = response;
                    window.newrelic.noticeError(e.message,
                      {
                        page: 'Adobe IMS',
                        api: getPpsProfileConfig().endpoint,
                        fatal: false,
                        user: profile.email,
                      });
                  })
                    .catch(ex => {
                      profile = ex;
                    })
    
                }
              }
    
              // Run callback
              if (afterSignInCallback) {
                let profile;
                adobeIMS.getProfile().then(response => {
                  profile = response;
                  afterSignInCallback(profile);
                })
                  .catch(ex => {
                    profile = ex;
                  })
    
              }
            }
        };
    this.init();
  }
  componentDidMount() {
    const {
      mandatorySignIn, susiLocale, afterSignInCallback, signInAction, signOutAction, addProfileAction
    } = this.props;

    const dispatchWindowEvent = (eventName) => {
      let event;
      if (typeof (Event) === 'function') {
        event = new Event(eventName);
      } else {
        event = document.createEvent('Event');
        event.initEvent(eventName, true, true);
      }
      window.dispatchEvent(event);
    };
    if (mandatorySignIn)
    {
      this.whenSignInMandatory();
    }
  }

  whenSignInMandatory = () => {
    if(window.adobeid.imsReady)
      {
        window.adobeIMS
        .getProfile()
        .then((profile) => {
          window.adobeid.onProfile(profile);
        })
        .catch((ex) => {
        if(!window.adobeIMS.isSignedInUser())
        {
          window.adobeIMS.signIn({},null);
        }
        });
      }
      else
      {
        window.setTimeout(this.whenSignInMandatory,100)
      }
  }



  init = () =>{
    // Load the IMS library
    window.adobeIMS = new AdobeIMS(this.imsData);
    window.adobeIMS.initialize();
    window.adobeid = this.imsData;
    window.adobeIMS.getUserProfile = fetchProfile ;
  }

  // Render the user's avatar
  renderAvatar = () => {
    const { ccProfile } = this.props;

    // If we weren't able to fetch the avatar, use a default.
    if (!ccProfile || !ccProfile.user || !ccProfile.user.avatar) {
      return (
        <div className="cmp-adobeIMS--initialAvatar" />
      );
    }

    return (
      <img
        className="cmp-adobeIMS--avatar"
        src={ccProfile.user.avatar}
        alt={Boolean(this.props.profile) ? this.props.profile.displayName :null  }
      />
    );
  }

  onSignInClick = () => {
    window.adobeIMS.signIn();
  };

  onSignOutClick = () => {
    window.adobeIMS.signOut();
  };

  renderButton = () => {
    const { isSignedIn } = this.props;

    // Adobe ID not initialized yet
    if (isSignedIn === null) {
      return <Wait />;
    }

    // Adobe ID signed out
    if (isSignedIn === false) {
      return (
        <div>
          <Button variant="cta" icon={<AdobeLogo />} onClick={this.onSignInClick}><Translate className="spectrum-Button-label" value="global.ims.sign_in" /></Button>
        </div>
      );
    }

    // Adobe ID signed in
    return (
      <div className="cmp-adobeIMS">
        <Dropdown
          trigger="click"
          closeOnSelect
          alignRight
          style={{
            display: 'inline-block',
          }}
        >
          <div className="cmp-adobeIMS--container">
            {this.renderAvatar()}
          </div>
          <Menu>
            <MenuItem
              role="menuitemradio"
              disabled
              className="cmp-adobeIMS--userInfo"
            >
              <span className="cmp-adobeIMS--name">{Boolean(this.props.profile) ? this.props.profile.displayName :null}</span>
              <span className="cmp-adobeIMS--email">{Boolean(this.props.profile) ? this.props.profile.email : null }</span>
            </MenuItem>
            <MenuItem
              role="menuitemradio"
              value="sign_out"
              onClick={this.onSignOutClick}
            >
              <Translate value="global.ims.sign_out" />
            </MenuItem>
          </Menu>
        </Dropdown>
      </div>
    );
  }

  render() {
    return this.renderButton();
  }
}

AdobeIMSV2.propTypes = {
  /**
   * mandatorySignIn {boolean} Redirect the user to sign-in if they are not already.
   */
  mandatorySignIn: PropTypes.bool,
  /**
   * susiLocale {string} Locale the IMS sign in page should use.
   */
  susiLocale: PropTypes.string.isRequired,
  /**
   * afterSignInCallback {function} An optional callback function to be called after
   * the user is signed in. Will return the user's profile as well.
   */
  afterSignInCallback: PropTypes.func,
  /**
   * isSignedIn {boolean} Returned from Redux mapStateToProps, returns whether the user is
   * signed in or not.
   */
  isSignedIn: PropTypes.bool,
  /**
   * ccProfile {object} Returned from Redux mapStateToProps, returns the CC object that
   * includes the user's avatar.
   */
  ccProfile: PropTypes.shape({
    user: PropTypes.object,
    sections: PropTypes.object,
  }),
  /**
   * signInAction {function} Returned from Redux mapDispatchToProps, allows the sign in
   * action to be called.
   */
  signInAction: PropTypes.func.isRequired,
  /**
   * signOutAction {function} Returned from Redux mapDispatchToProps, allows the sign out
   * action to be called.
   */
  signOutAction: PropTypes.func.isRequired,
};
AdobeIMSV2.defaultProps = {
  mandatorySignIn: false,
  afterSignInCallback: null,
  isSignedIn: null,
  ccProfile: null,
};

const mapStateToProps = (state) => ({
  isSignedIn: state.ims.signedIn,
  ccProfile: state.ims.profile,
  profile: state.ims.userProfile,
});

const mapDispatchToProps = (dispatch) => ({
  signInAction: (e) => dispatch(signIn(e)),
  signOutAction: () => dispatch(signOut()),
  addProfileAction: (e) => dispatch(addProfile(e)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdobeIMSV2);
